import config from "../config/consent-config";

const CONSENTSTORAGEKEY = "userConsentData";
// 30 mins
const CONSENTDATAEXP = 30 * 60 * 1000;

// Client Region
const getClientRegion = () => {
    const MAX_WAIT = 30 * 1000; // 30s timeout for adblockers / network blocks
    const REGION_DEFAULT = { region: "PH", type: "" };
    const APIURL = "https://api.country.is?fields=subdivision";

	return new Promise((resolve) => {
        let resolved = false;

        const safeResolve = (value) => {
            if (!resolved) {
                resolved = true;
                resolve(value);
            }
        };
        // Testing
        //safeResolve({ region: "GB", type: "TCF" });
        //safeResolve({ region: "US-CA", type: "CPRA" });
        //safeResolve({ region: "US-VA", type: "VCDPA" });
        //safeResolve({ region: "PH", type: "" });
        //return;

        // Timeout fallback — guarantees the promise ALWAYS resolves
        const timeout = setTimeout(() => {
            safeResolve(REGION_DEFAULT);
        }, MAX_WAIT);

        try {
            // Docs: https://country.is/
            /*
                * No quotas. Infrastructure rate-limits to 10 requests per second per IP to prevent abuse.
                */
            const req = () => {
                var options = {
                    method: "GET"
                };
                fetch(APIURL, options)
                .then((response) => {
                    if (!response.ok) {
                        clearTimeout(timeout);
                        safeResolve(REGION_DEFAULT);
                    }
                    return response.json();
                })
                .then((data) => {
                    clearTimeout(timeout);

                    // Validate response shape
                    if (!data || typeof data !== "object") {
                        return safeResolve(REGION_DEFAULT);
                    }

                    if (data.error) {
                        return safeResolve(REGION_DEFAULT);
                    }

                    const countryCode = data.country || "";
                    const regionCode = data.subdivision || "";

                    let cmpType = "";

                    // United States (CPRA / VCDPA)
                    if (countryCode === "US") {
                        if (regionCode === "CA") {
                            cmpType = "CPRA";
                        } else if (["VA", "CO", "CT", "UT"].includes(regionCode)) {
                            cmpType = "VCDPA";
                        }

                        return safeResolve({ region: `US-${regionCode}`, type: cmpType });
                    }

                    // EU / UK (TCF)
                    if (["GB", "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
                        "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE"
                    ].includes(countryCode)) {
                        return safeResolve({ region: countryCode, type: "TCF" });
                    }

                    // Rest of world
                    return safeResolve({ region: countryCode, type: "" })
                })
                .catch(() => {
                    clearTimeout(timeout);
                    safeResolve(REGION_DEFAULT);
                });

            }
            return req();
        } catch (err) {
            clearTimeout(timeout);
            safeResolve(REGION_DEFAULT);
        }
    });
}


const fetchConsent = (userId, type = "cookie", useApi = true) => {
    /*
        * userId = cookie user id
        * type = type of consent
        *      - 'cookie' for general cookies
        *      - '...' for checkout
        *      - '...' for franchise/course registration
        * useApi = whether to trigger an API call
        */


    // Make it URL safe
    const encodedUserId = encodeURIComponent(userId);

    // Store user's consent data to localstorage to avoid multiple API calls

    /*
        * If user consent data is already stored in the localstorage, check expiration.
        * If not expired, return the cached list.
        * Otherwise, make an API call to fetch consent data, then store to localstorage
        */
    return new Promise(async (resolve, reject) => {
        let userConsentData = localStorage.getItem(CONSENTSTORAGEKEY);

        let useCache = false;

        if (userConsentData !== null) {
            try {
                userConsentData = JSON.parse(userConsentData);

                if (Date.now() < userConsentData.exp) {
                    useCache = true;
                }
            } catch (error) { }
        }

        if (useCache === true) {
            // If there is a non-expired cache
            resolve(userConsentData);
        } else {
            // If cache is expired or no cache
            try {
                let data = userConsentData ?? {};

                /*
                // TODO
                if (useApi) {
                    data = await this.request.send(
                        `${this.endpoint}/${type}/main?userId=${encodedUserId}`,
                        "GET"
                    )
                }
                */

                if (data.hasOwnProperty("consent")) {
                    // For future: handle different consent types, different keys
                    const exp = Date.now() + CONSENTDATAEXP;
                    localStorage.setItem(CONSENTSTORAGEKEY, JSON.stringify({ ...data, exp }));
                }

                resolve(data);
            } catch (err) {
                reject(err);
            }
        }
    });
}

const writeConsent = (userId, consent, method = "PUT", type = "cookie", useApi = true) => {
    /*
        * userId = cookie user id
        * consent = consent object
        * method = "POST" (new consent), "PUT" (existing consent)
        * type = type of consent
        *      - 'cookie' for general cookies
        *      - '...' for checkout
        *      - '...' for franchise/course registration
        * useApi = whether to trigger an API call
        */

    const body = {
        userId,
        consent
    }

    return new Promise(async (resolve, reject) => {
        try {
            let data = {};

            /*
            TODO
            if (useApi) {
                data = await this.request.send(
                    `${this.endpoint}/${type}/main`,
                    method,
                    body
                )
            } else  {
                data = { ...body, updatedAt: new Date().toISOString() }
            }
            */
            data = { ...body, updatedAt: new Date().toISOString() }

            if (data.hasOwnProperty('consent')) {
                const exp = Date.now() + CONSENTDATAEXP;
                localStorage.setItem(CONSENTSTORAGEKEY, JSON.stringify({ ...data, exp }));
            }

            resolve(data);
        } catch (err) {
            reject(err)
        }
    });
}


// Get cookie user id
const getUserId = () => {
    if (typeof document === "undefined") return null;
    const name = "cookieconsent_userid="

    // Search from cookie
    const cookies = document.cookie.split(";");
    for (let c of cookies) {
        c = c.trim()
        if (c.startsWith(name)) {
            // Get the value of the cookie
            return c.substring(name.length)
        }
    }

    // If there is no cookie, generate one and store it
    const uuid = crypto.randomUUID();
    document.cookie = `cookieconsent_userid=${uuid}; Path=/; SameSite=Lax; Secure; Max-Age=31536000`

    return uuid
}

const generateDefaultConsent = (acceptall = false, regionData) => {
    /*
     * If regionData is present, follow the defaults by region
     * Otherwise, follow the acceptall boolean
     */
    if (regionData === undefined) {
        return generateConsentObj(acceptall);
    }
    const regiontype = regionData.type === "" ? "DEFAULT" : regionData.type;
    let tmpObj = generateConsentObj(false); // baseline reject all, then override
    if (regiontype === "TCF") {
        // EU / GDPR / TCF; unofficial TCF CMP should still follow this
        tmpObj.ads_data_redaction = true;
    } else {
        // Grant analytics if non-tcf
        tmpObj.analytics_storage = "granted";
        if (regiontype === "CPRA") {
            // California (CPRA)
            tmpObj.ads_data_redaction = true;
            tmpObj.sale_opt_out = false;
            tmpObj.sensitive_data_limit = false;
        } else if (regiontype === "VCDPA") {
            // VCDPA / Colorado / Virginia
            tmpObj.ad_storage = "granted";
            tmpObj.sale_opt_out = false;
        }
        // Maybe add more in the future
    }
    return tmpObj;
}

const generateConsentObj = (acceptall = false) => {
    const purposelist = getTCFPurposes();
    const grantedstr = acceptall ? "granted" : "denied";

    return {
        analytics_storage: grantedstr,
        ad_storage: grantedstr,
        ad_user_data: grantedstr,

        ad_personalization: grantedstr,
        sale_opt_out: !acceptall,
        sharing_opt_out: !acceptall, // Reverse of targeted_advertising
        targeted_advertising: grantedstr,
        profiling: grantedstr,

        functionality_storage: grantedstr,
        personalization_storage: grantedstr,

        // For Accept All / Reject All, these will be set outside
        sensitive_data_limit: true, // Always TRUE for TCF, can only be set to TRUE by VCDPA;  set to true for OTHERS to be safe because Brazil (LGPD), South Korea (PIPA), Thailand (PDPA), Singapore (PDPA).
        ads_data_redaction: false, // True only when (TCF or CPRA) && consent.ad_storage !== "granted"

        security_storage: "granted", // Always granted
        tcf: {
            purposes: purposelist.all.reduce((out, id) => {
                // Auto-check purposes not requiring consent under TCF v2.2
                out[id] = [5, 6, 8, 9, 10].includes(id) ? true : acceptall;
                return out;
            }, {}),
            legitimateInterests: {},
            specialFeatures: {},
            vendors: config.tcfvendorlist.reduce((out, item) => {
                out[item.vendorid] = acceptall;
                return out;
            }, {}),
            vendorLegitimateInterests: {},
            specialPurposes: { 1: true, 2: true, 3: true } // Always granted
        },
        nontcf: {
            vendors: config.tcfnonvendorlist.reduce((out, item) => {
                out[item.vendorid] = acceptall;
                return out;
            }, {})
        }
    };
}


const getTCFPurposes = () => {
    const vendors = [...config.tcfvendorlist, ...config.tcfnonvendorlist];
    const allPurposes = vendors.flatMap(v => v.purposeidlist);
    const unique = [...new Set(allPurposes)];

    // Only purposes requiring consent under TCF v2.2
    const consentPurposes = [1, 3, 4, 7];

    return {
        consent: unique.filter(p => consentPurposes.includes(p)).sort((a, b) => a - b),
        all: unique
    };
}

// Assumes Google Tag Manager (GTM) - Data Layer
const pushConsentUpdate = (userId, consent) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
        event: "consent_update",
        userId,
        consent,
    })
}

// This is done after saving to database
const pushConsentWrite = (userId, consent, mode = "PUT") => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
        event: "consent_write",
        userId,
        mode,
        consent,
    })
}

const applyGoogleConsent = (consent, initialization = false) => {
    // Already checked outside
    //if (typeof window === "undefined") return;
    //window.gtag = window.gtag || function() { window.dataLayer.push(arguments); };

    window.gtag("consent", initialization ? "default" : "update", {
        ad_storage: consent.ad_storage,
        analytics_storage: consent.analytics_storage,
        ad_user_data: consent.ad_user_data,
        ad_personalization: consent.ad_personalization,
        functionality_storage: consent.functionality_storage,
        personalization_storage: consent.personalization_storage,
        security_storage: "granted", // Always granted
        wait_for_update: initialization ? 2000 : 500 // Recommended for GTM
    });

    // TODO: Own control/field; "Safe" mode for now
    window.gtag("set", "ads_data_redaction", consent.ads_data_redaction);
}

export {
    getClientRegion,
    fetchConsent,
    writeConsent,
    getUserId,
    generateDefaultConsent,
    getTCFPurposes,
    pushConsentUpdate,
    pushConsentWrite,
    applyGoogleConsent
}